/**
 * The game log as the screen walks it: each snap becomes a PlayView to animate, and every other
 * event (toss, kickoff, subs, kicks, scores, possession, quarters, the final) a line for the text
 * box. Fed one call's events at a time; every word comes from the sim (gameEventLine, downName,
 * sideSpotName).
 */
import {
  DEFAULT_PLAYBOOK,
  downName,
  gameEventLine,
  sideSpotName,
  type GameEvent,
  type Team,
  type TeamId,
  type UnitId,
} from '@tdmons/sim';
import { viewOf, type PlayView } from '../field/playView';

export type Step =
  | { kind: 'snap'; view: PlayView; event: Extract<GameEvent, { type: 'snap' }> }
  | { kind: 'note'; line: string; event: GameEvent };

/** "ELECTABUZZ" from "Team Electabuzz": a team's name on the field. */
export const teamTag = (t: Team): string => t.name.replace(/^Team /, '').toUpperCase();

const playName = (id: string, plays: readonly { id: string; name: string }[]) =>
  plays.find((p) => p.id === id)?.name ?? id;

export class StepBuilder {
  /** The offense charts as the game's subs leave them, to name a kicker in his slot. */
  private readonly charts: Record<TeamId, Record<string, number>>;

  constructor(
    private readonly teams: Record<TeamId, Team>,
    private readonly seed: number,
  ) {
    this.charts = { A: { ...teams.A.offense }, B: { ...teams.B.offense } };
  }

  /** The steps for these events, in order. */
  push(events: readonly GameEvent[]): Step[] {
    const steps: Step[] = [];
    for (const e of events) {
      if (e.type === 'snap') {
        steps.push({ kind: 'snap', view: this.snapView(e), event: e });
        continue;
      }
      if (e.type === 'sub' && e.side === 'offense') {
        const chart = this.charts[e.team];
        const from = Object.keys(chart).find((s) => chart[s] === e.in);
        if (from !== undefined) chart[from] = e.out;
        chart[e.slot] = e.in;
      }
      const line = gameEventLine(e, this.teams, (u) => this.kicker(e, u));
      if (line) steps.push({ kind: 'note', line, event: e });
    }
    return steps;
  }

  private snapView(e: Extract<GameEvent, { type: 'snap' }>): PlayView {
    const title = `${playName(e.offensePlay, DEFAULT_PLAYBOOK.offense)} vs ${playName(e.defensePlay, DEFAULT_PLAYBOOK.defense)}`;
    const defense: TeamId = e.at.offense === 'A' ? 'B' : 'A';
    const down =
      e.kind === 'two-point' ? 'Two-point try' : downName(e.at.down, e.at.toGo, e.at.ballOn);
    return {
      ...viewOf(this.seed, e.before, e.outcome, title),
      situation: `${teamTag(this.teams[e.at.offense])} ${down} at ${sideSpotName(e.at.ballOn, teamTag(this.teams[defense]))}`,
    };
  }

  private kicker(e: GameEvent, u: UnitId): string {
    if (!('at' in e)) return 'The kicker';
    const slot = u.split(':')[1] as keyof Team['offense'];
    const i = this.charts[e.at.offense][slot];
    return i === undefined
      ? 'The kicker'
      : (this.teams[e.at.offense].roster[i]?.name ?? 'The kicker');
  }
}
